import { Avatar, CardActionArea, Chip, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { getNightOrDayPalette } from '../utils/getNightOrDayPalette';
import { LightbulbOutlined, Link, LinkOff } from '@mui/icons-material';
import { FC } from 'react';
import { graphql } from '../gql';
import { PriorityItem } from '../actions/inputs/PrioritySelect';
import { useActionStatusOptions } from '../actions/inputs/useActionStatusOptions';
import { useActionPriorityOptions } from '../actions/inputs/useActionPriorityOptions';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import { StatusItem } from '../actions/inputs/StatusSelect';
import { isActionExpired } from '../actions/isActionExpired';
import { getExpiredBgColor } from '../utils/getExpiredBgColor';
import { useSnackbar } from '../store/snackbar';

const opportunityActionsList_getOpportunity = graphql(`
	query opportunityActionsList_getOpportunity($opportunityId: UUID!) {
		getOpportunity(by: { id: $opportunityId }) {
			actions {
				id
				name
				status
				deadline
				priority
				owner {
					name
					avatar {
						url(width: 24, height: 24)
					}
				}
			}
		}
	}
`);

const opportunityActionsList_removeOpportunityAction = graphql(`
	mutation opportunityActionssList_removeOpportunityAction($opportunityId: UUID!, $actionId: UUID!) {
		updateOpportunity(by: { id: $opportunityId }, data: { actions: { disconnect: { id: $actionId } } }) {
			ok
		}
	}
`);

type ActionsListProps = Readonly<{
	opportunityId: string;
	size: 'medium' | 'small';
}>;

export const ActionsList: FC<ActionsListProps> = ({ opportunityId, size }) => {
	const intl = useIntl();
	const theme = useTheme();
	const navigate = useNavigate();
	const { statusOptions } = useActionStatusOptions();
	const { priorityOptions } = useActionPriorityOptions();
	const { openSnackbar } = useSnackbar();

	const { data: opportunity } = useApiQuery(opportunityActionsList_getOpportunity, {
		variables: { opportunityId },
	});
	const { mutate: removeAction } = useApiMutation(opportunityActionsList_removeOpportunityAction);

	const openAction = (actionId: string) => {
		navigate(`/actions/${actionId}/detail`);
	};

	const handleRemoveAction = (actionId: string) => {
		removeAction(
			{ opportunityId, actionId },
			{
				onSuccess: (result) =>
					mutationResponseHandler({
						result: result.updateOpportunity.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'opportunity.remove-action.success' }),
							error: intl.formatMessage({ id: 'opportunity.remove-action.error' }),
						},
						openSnackbar,
					}),
			},
		);
	};

	return (
		(opportunity?.getOpportunity?.actions.length ?? 0) > 0 && (
			<Stack direction={'column'} gap={1.5}>
				{opportunity?.getOpportunity?.actions.map((action) => {
					const status = statusOptions.find((s) => s.value === action.status)!;
					const priority = priorityOptions.find((p) => p.value === action.priority)!;
					const isExpired = isActionExpired(action.deadline ?? '');

					return (
						<CardActionArea key={action.id} onClick={() => openAction(action.id)} sx={{ borderRadius: '6px' }}>
							<Stack
								direction={'row'}
								alignItems={'center'}
								gap={size === 'medium' ? 2.5 : 1.5}
								sx={{
									border: `1px solid ${theme.palette.grey[300]}`,
									borderRadius: '6px',
									padding: size === 'medium' ? '14px 20px 14px 20px' : '10px 8px 10px 12px',
									backgroundColor: isExpired ? getExpiredBgColor(theme) : undefined,
								}}
							>
								<Stack flexGrow={1} direction={'row'} gap={size === 'medium' ? 1.5 : 1} alignItems={'center'}>
									<LightbulbOutlined sx={{ fontSize: '20px', color: theme.palette.secondary.main }} />
									<Typography
										variant={'h6'}
										fontWeight={500}
										sx={{
											display: '-webkit-box',
											overflow: 'hidden',
											WebkitBoxOrient: 'vertical',
											WebkitLineClamp: 2,
										}}
									>
										{action.name}
									</Typography>
								</Stack>
								{size === 'medium' ? (
									<>
										<StatusItem {...status} />
										<PriorityItem {...priority} />
									</>
								) : (
									<Stack direction={'row'} gap={0.5}>
										<Chip
											label={priority.text}
											size={'small'}
											sx={{ color: theme.palette[getNightOrDayPalette(theme)][0], backgroundColor: priority.color }}
										/>
										<Chip
											label={status.text}
											size={'small'}
											sx={{ color: theme.palette[getNightOrDayPalette(theme)][0], backgroundColor: status.color }}
										/>
									</Stack>
								)}
								{size === 'medium' ? (
									<Stack
										justifyContent={'center'}
										alignItems={'center'}
										sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: '50%', minWidth: '32px', minHeight: '32px' }}
									>
										<Avatar alt={action.owner?.name} src={action.owner?.avatar?.url ?? undefined} sx={{ width: '24px', height: '24px' }} />
									</Stack>
								) : (
									<Avatar alt={action.owner?.name} src={action.owner?.avatar?.url ?? undefined} sx={{ width: '24px', height: '24px' }} />
								)}
								<IconButton
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveAction(action.id);
									}}
									sx={{
										'&:not(:hover) > :nth-child(2)': { display: 'none' },
										'&:hover > :first-of-type': { display: 'none' },
										padding: '6px',
									}}
								>
									<Link sx={{ fill: theme.palette.secondary.main, fontSize: '20px' }} />
									<LinkOff sx={{ fill: theme.palette.secondary.main, fontSize: '20px' }} />
								</IconButton>
							</Stack>
						</CardActionArea>
					);
				})}
			</Stack>
		)
	);
};
